import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import dayjs from 'dayjs';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import ContentRelated from 'components/ContentRelated';
import { useCallContentRelated } from 'components/Dashboard/utilities/callContentRelated';
import useCallRelatedStakeholders from 'components/Dashboard/KeyUpdates/useCallStakeholders';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { store } from 'components/Store';
import InteractionFormFields, { InteractionFormFieldsSkeleton } from './InteractionFormFields';

const CrmInteractionDetail = () => {
  const { interactionId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    date: '',
    type: '',
    description: '',
    initiator: '',
    stakeholders: [],
    members: [],
  });

  const initialFormRef = useRef(null);
  const [isBaselineReady, setIsBaselineReady] = useState(false);
  const baselineTimeoutRef = useRef(null);

  const { state, dispatch } = useContext(store);
  const { generalApiCall } = useGeneralApiCall();
  const [heightContainer, containerRef] = useHeightContainer();
  const { contentResults, setContentResults } = useContentResults();
  const [isLoadingRelatedContent, setIsLoadingRelatedContent] = useState(true);
  const { callContentRelated } = useCallContentRelated();
  const { callRelatedStakeholders } = useCallRelatedStakeholders();
  const numberOfStakeHoldersToShow = parseInt(import.meta.env.VITE_APP_RELATED_STAKEHOLDERS_TO_SHOW);
  const crmSection = contentResults['CrmInteractions'];

  const loadInteraction = useRef(null);
  loadInteraction.current = async (source) => {
    try {
      setIsLoading(true);
      setIsLoadingRelatedContent(true);
      const result = await generalApiCall({
        method: 'get',
        pathname: `/api/crm-interaction/${interactionId}`,
        needsAuthentication: true,
        requestSource: source,
        notShowErrorMessage: true,
      });
      if (result) {
        const loadedForm = {
          title: result.title ?? '',
          date: result.dateTime ? dayjs(result.dateTime).format('YYYY-MM-DD') : '',
          type: result.interactionType ?? '',
          description: result.htmlBody ?? result.description ?? '',
          initiator:
            result.initiatedByOrganisation === true
              ? 'Organisation'
              : result.initiatedByOrganisation === false
                ? 'Stakeholder'
                : '',
          stakeholders: (result.stakeholderContacts ?? []).map((c) => ({
            value: c.id,
            label: c.fullName ?? '',
            id: c.id,
            email: '',
            imageId: c.imageId ?? null,
            imageUrl: null,
            stakeholderContactId: c.stakeholderContactId ?? null,
            party: c.party ?? null,
          })),
          members: (result.associatedUserIds ?? []).map((id, idx) => {
            const nameFromResponse = result.associatedUserFullNames?.[idx];
            const member = !nameFromResponse ? state.team?.allMembers?.find((m) => m.id === id) : null;
            return { value: id, label: nameFromResponse ?? member?.fullName ?? member?.emailAddress ?? `User ${id}` };
          }),
        };
        setForm(loadedForm);
        initialFormRef.current = loadedForm;
        setIsLoading(false);
        setIsBaselineReady(false);
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'interactionDetail',
          value: { title: loadedForm.title, sourceCrmEmailId: result.sourceCrmEmailId ?? null },
        });
        await loadRelatedContent(result, source);
        setIsLoading(false);
        setIsLoadingRelatedContent(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    loadInteraction.current(source);
    return () => {
      source.abort('Interaction detail cancelled');
      dispatch({ type: 'MODIFY_SECTION', parameter: 'interactionDetail', value: null });
      clearTimeout(baselineTimeoutRef.current);
    };
  }, [interactionId]);

  // The rich text editor normalises the loaded HTML (mail-merge widgets, quote/attribute formatting)
  // over one or more ticks after it mounts, which would otherwise register as a false "unsaved change"
  // against the raw API value. Rather than guessing a fixed delay, wait for form to stop changing
  // for a bit, then treat wherever it settled as the real baseline.
  useEffect(() => {
    if (isLoading || isBaselineReady) return;
    clearTimeout(baselineTimeoutRef.current);
    baselineTimeoutRef.current = setTimeout(() => {
      initialFormRef.current = form;
      setIsBaselineReady(true);
    }, 400);
    return () => clearTimeout(baselineTimeoutRef.current);
  }, [form, isLoading, isBaselineReady]);

  const loadRelatedContent = async (interaction, source) => {
    const stakeholderIds = interaction.contactIds ?? [];

    const contentRelated = await callContentRelated({
      objectId: `CrmInteraction-${interactionId}`,
      objectType: 'CrmInteraction',
      source,
    });

    let relatedContentWithStakeholders = contentRelated ?? { hits: {} };
    let stakeholdersRendered = [];
    let contactsIdsCalled = [];

    if (stakeholderIds.length > 0) {
      const initialIds = stakeholderIds.slice(0, numberOfStakeHoldersToShow);
      contactsIdsCalled = initialIds;
      const filters = initialIds.map((id) => ({ field: 'id', value: `${id}`, operator: 'str_eq' }));
      const stakeHolderResults = await callRelatedStakeholders({ stakeholderFilters: filters, source });
      stakeholdersRendered = stakeHolderResults?.data?.hits ?? [];
      relatedContentWithStakeholders = {
        ...relatedContentWithStakeholders,
        hits: { ...relatedContentWithStakeholders.hits, StakeHolders: stakeholdersRendered },
      };
    }

    setContentResults((prev) => ({
      ...prev,
      CrmInteractions: {
        ...prev.CrmInteractions,
        contentRelated: relatedContentWithStakeholders,
        relatedStakeholders:
          stakeholderIds.length > 0 ? stakeholderIds.filter((id) => !contactsIdsCalled.includes(id)) : null,
        relatedStakeholdersRendered: stakeholdersRendered.length,
      },
    }));
    setIsLoadingRelatedContent(false);
  };

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (field === 'title') {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'interactionDetail',
        value: { ...state.interactionDetail, title: value },
      });
    }
  };

  const handleCancel = () => {
    setForm(initialFormRef.current);
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'interactionDetail',
      value: { ...state.interactionDetail, title: initialFormRef.current.title },
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      createNotification('danger', 'Please enter a title');
      return;
    }
    if (!form.type) {
      createNotification('danger', 'Please select an interaction type');
      return;
    }
    if (!form.date) {
      createNotification('danger', 'Please select a date');
      return;
    }
    setIsSaving(true);
    const result = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/update',
      requestProperties: {
        id: Number(interactionId),
        title: form.title,
        date: form.date,
        type: form.type,
        description: form.description,
        initiatedByOrganisation:
          form.initiator === 'Organisation' ? true : form.initiator === 'Stakeholder' ? false : null,
        contactIds: form.stakeholders.map((p) => p.id),
        userIds: form.members.map((m) => m.value),
      },
      needsAuthentication: true,
    });
    setIsSaving(false);
    if (result) {
      initialFormRef.current = form;
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'interactionDetail',
        value: { ...state.interactionDetail, title: form.title },
      });
      createNotification('success', 'Interaction saved');
    }
  };

  const isDirty = isBaselineReady && JSON.stringify(form) !== JSON.stringify(initialFormRef.current);
  const SAVE_BAR_HEIGHT = 68;

  const contentRelatedData = {
    objectId: `CrmInteraction-${interactionId}`,
    objectType: 'CrmInteraction',
    id: Number(interactionId),
    title: form.title,
    contentUrl: `/people/interactions/${interactionId}`,
  };

  return (
    <div className='main-content px-md-4 px-3'>
      <div className='row px-0 piece-content-container' ref={containerRef}>
        <div className='general-content-column padding-to-nav p-0 z-100'>
          <div className='content-container h-100'>
            <CustomScrollbar
              className='simple-scrollbar indented-scrollbar-small'
              style={{ height: `${heightContainer - (isDirty ? SAVE_BAR_HEIGHT : 0)}px` }}
              maximalThumbYSize={100}
            >
              <div className='p-3 p-lg-4 pb-3 pb-lg-5'>
                {isLoading ? (
                  <InteractionFormFieldsSkeleton />
                ) : (
                  <InteractionFormFields form={form} onChange={handleChange} />
                )}
                {!isDirty && (
                  <div className='d-flex justify-content-end'>
                    <button
                      className='general-button action-button rounded-pill py-2 px-4 paragraph-p2'
                      onClick={handleSave}
                      disabled={isSaving || !isDirty}
                    >
                      {isSaving ? 'Updating...' : 'Update interaction'}
                    </button>
                  </div>
                )}
              </div>
            </CustomScrollbar>
            {isDirty && (
              <div
                className='d-flex justify-content-start justify-content-lg-end align-items-center px-3 px-lg-4'
                style={{
                  height: `${SAVE_BAR_HEIGHT}px`,
                  position: 'relative',
                  zIndex: 10,
                  backgroundColor: 'rgba(0, 148, 204, 0.04)',
                  boxShadow: '0 -1px 3px rgba(60, 63, 66, 0.12), 0 -4px 10px rgba(60, 63, 66, 0.05)',
                }}
              >
                <button
                  className='general-button border rounded-pill py-2 px-4 paragraph-p2 mr-3'
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className='general-button action-button rounded-pill py-2 px-4 paragraph-p2'
                  onClick={handleSave}
                  disabled={isSaving || !isDirty}
                >
                  {isSaving ? 'Updating...' : 'Update interaction'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className='related-content-column show-lg px-0'>
          <ContentRelated
            data={contentRelatedData}
            content={crmSection?.contentRelated}
            relatedStakeholders={crmSection?.relatedStakeholders}
            relatedStakeholdersRendered={crmSection?.relatedStakeholdersRendered ?? 0}
            mainSection='CrmInteractions'
            isLoadingRelatedContent={isLoadingRelatedContent}
          />
        </div>
      </div>
    </div>
  );
};

export default CrmInteractionDetail;
